import React, { useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Table,
  Statistic,
  Space,
  Button,
  Tooltip,
  Spin,
  Select
} from "antd";
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined, 
  MinusOutlined,
  QuestionCircleOutlined 
} from "@ant-design/icons";
import moment from "moment";
import ENVIRONMENT from '../../../../environments';
import axios from 'axios';
import { processChartData } from "./chartUtils";

const { Title, Text } = Typography;
const { Option } = Select;

// Specialized function for ThroughputDetails that fills missing periods with 0 values
const processThroughputData = (sourceData, viewTypeFilter, dateFormat = "YYYY-MM-DD", startDate = null, endDate = null) => {
  if (!sourceData?.length) return [];

  if (viewTypeFilter === "Daily") {
    return processChartData(sourceData, viewTypeFilter, dateFormat, startDate, endDate);
  }

  // Helper function to aggregate data by period with complete coverage
  const aggregateByPeriod = (periodType) => {
    const aggregatedData = new Map();
    const types = [...new Set(sourceData.map(item => item.type))];
    
    // Process existing data
    sourceData.forEach((item) => {
      const itemDate = moment(item.date2, dateFormat === "DD/MM/YYYY" ? "YYYY/MM/DD" : "YYYY-MM-DD");
      const periodStart = itemDate.startOf(periodType);
      const periodKey = `${periodStart.format('YYYY/MM/DD')}_${item.type}`;
      
      let periodLabel, sortDate;
      if (periodType === 'week') {
        const weekEnd = periodStart.clone().endOf('week');
        periodLabel = `${periodStart.format('MMM DD')} - ${weekEnd.format('MMM DD')} ${periodStart.year()}`;
        sortDate = periodStart.format('YYYY/MM/DD');
      } else { // month
        periodLabel = periodStart.format('MMM YYYY');
        sortDate = periodStart.format('YYYY/MM/DD');
      }
      
      if (!aggregatedData.has(periodKey)) {
        aggregatedData.set(periodKey, {
          date2: periodLabel,
          type: item.type,
          value: 0,
          sortDate: sortDate
        });
      }
      aggregatedData.get(periodKey).value += item.value;
    });
    
    // Fill in missing periods within the date range
    if (startDate && endDate) {
      const start = moment(startDate);
      const end = moment(endDate);
      const periodStart = start.clone().startOf(periodType);
      const periodEnd = end.clone().endOf(periodType);
      
      for (let currentPeriod = periodStart.clone(); currentPeriod.isSameOrBefore(periodEnd); currentPeriod.add(1, periodType)) {
        let periodLabel, periodKey;
        if (periodType === 'week') {
          const weekEnd = currentPeriod.clone().endOf('week');
          periodLabel = `${currentPeriod.format('MMM DD')} - ${weekEnd.format('MMM DD')} ${currentPeriod.year()}`;
          periodKey = currentPeriod.format('YYYY/MM/DD');
        } else { // month
          periodLabel = currentPeriod.format('MMM YYYY');
          periodKey = currentPeriod.format('YYYY/MM');
        }
        
        types.forEach(type => {
          const fullKey = `${periodKey}_${type}`;
          if (!aggregatedData.has(fullKey)) {
            aggregatedData.set(fullKey, {
              date2: periodLabel,
              type: type,
              value: 0,
              sortDate: currentPeriod.format('YYYY/MM/DD')
            });
          }
        });
      }
    }
    
    return Array.from(aggregatedData.values()).sort((a, b) => 
      moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
    );
  };

  if (viewTypeFilter === "Weekly") {
    return aggregateByPeriod('week');
  }

  if (viewTypeFilter === "Monthly") {
    return aggregateByPeriod('month');
  }

  return sourceData;
};

const ThroughputDetails = ({ 
  chartStyle, 
  companyList, 
  masterDateRange = null, 
  masterPresetRange = null,
  masterCompanyFilter = null
}) => {
  const [tableViewType, setTableViewType] = useState("Daily");
  const [throughputData, setThroughputData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentData, setRecentData] = useState({
    yesterday: { value: 0, trend: 0 },
    last7Days: { value: 0, trend: 0 },
    last30Days: { value: 0, trend: 0 }
  });
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);

  const effectiveStartRange = masterDateRange?.start
  const effectiveEndRange = masterDateRange?.end
  const effectiveCompanyFilter = masterCompanyFilter;

  // Combined loading state for better UX
  const isAnyLoading = isLoading || isLoadingRecent;

  // Helper function to format dates for horizontal table
  // Helper function to format date for tooltip
  const formatDateForTooltip = (period) => {
    // Handle different date formats
    if (period.includes('/')) {
      // Format: YYYY/MM/DD -> MM/DD/YYYY
      const parts = period.split('/');
      if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      }
    }
    // For weekly and monthly formats, return as is
    return period;
  };

  const formatPeriodForTable = (period, viewType) => {
    if (viewType === "Daily") {
      // Format: "01/15" or "Jan 15"
      const date = moment(period, "YYYY/MM/DD");
      return date.format("MM/DD");
    } else if (viewType === "Weekly") {
      // Format: "Jan 15-21" (shorter version)
      const parts = period.split(" - ");
      if (parts.length === 2) {
        const startDate = moment(parts[0] + " " + parts[1].split(" ")[2], "MMM DD YYYY");
        const endDate = moment(parts[1], "MMM DD YYYY");
        return `${startDate.format("MMM DD")}-${endDate.format("DD")}`;
      }
      return period;
    } else if (viewType === "Monthly") {
      // Format: "Jan 2025" -> "Jan"
      const date = moment(period, "MMM YYYY");
      return date.format("MMM");
    }
    return period;
  };

  // Calculate averages from the main data
  const averages = useMemo(() => {
    if (!throughputData.length) return {};

    const startDate = moment(effectiveStartRange);
    const endDate = moment(effectiveEndRange);
    
    // Calculate total days in the date range
    const totalDays = endDate.diff(startDate, 'days') + 1;
    const totalValue = throughputData.reduce((sum, item) => sum + (item.value || 0), 0);
    
    // Calculate weekly average only if more than 7 days
    let weeklyAverage = null;
    if (totalDays >= 7) {
      const totalWeeks = totalDays / 7;
      weeklyAverage = totalValue / totalWeeks;
    }
    
    // Calculate monthly average only if more than 30 days
    let monthlyAverage = null;
    if (totalDays >= 30) {
      const totalMonths = totalDays / 30;
      monthlyAverage = totalValue / totalMonths;
    }
    
    return {
      daily: totalDays > 0 ? (totalValue / totalDays).toFixed(1) : 0,
      weekly: weeklyAverage ? weeklyAverage.toFixed(1) : null,
      monthly: monthlyAverage ? monthlyAverage.toFixed(1) : null
    };
  }, [throughputData, effectiveStartRange, effectiveEndRange]);

  // Fetch main throughput data
  const fetchThroughputData = async () => {
    if (!effectiveStartRange || !effectiveEndRange) return;
    
    try {
      setIsLoading(true);
      const startDate = moment(effectiveStartRange).format("YYYY-MM-DD");
      const endDate = moment(effectiveEndRange).format("YYYY-MM-DD");
      
      let payload = {
        action: "get_products_models_delivered_throughput",
        start_date: startDate,
        end_date: endDate
      };

      if (effectiveCompanyFilter && effectiveCompanyFilter !== "ALL") {
        payload.company_id = effectiveCompanyFilter;
      }

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      setThroughputData(response.data || []);
    } catch (error) {
      console.error("Error fetching throughput data:", error);
      setThroughputData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch recent data for trend calculation
  const fetchRecentData = async () => {
    try {
      setIsLoadingRecent(true);
      const now = moment();
      
      // Get last 30 days data in one API call (excluding today for yesterday calculation)
      const last30dStart = now.clone().subtract(30, 'days');
      const last30dEnd = now.clone().subtract(1, 'day'); // Exclude today

      let payload = {
        action: "get_products_models_delivered_throughput",
        start_date: last30dStart.format("YYYY-MM-DD"),
        end_date: last30dEnd.format("YYYY-MM-DD")
      };

      if (effectiveCompanyFilter && effectiveCompanyFilter !== "ALL") {
        payload.company_id = effectiveCompanyFilter;
      }

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      // Calculate yesterday data (previous day)
      const yesterdayStart = now.clone().subtract(1, 'day').startOf('day');
      const yesterdayEnd = now.clone().subtract(1, 'day').endOf('day');
      
      const yesterdayData = data.filter(item => {
        const itemDate = moment(item.date2, "YYYY/MM/DD");
        return itemDate.isBetween(yesterdayStart, yesterdayEnd, 'day', '[]');
      });
      const yesterdayValue = yesterdayData.reduce((sum, item) => sum + (item.value || 0), 0);
      
      // Calculate last 7 days data (excluding today)
      const last7dStart = now.clone().subtract(7, 'days');
      const last7dData = data.filter(item => {
        const itemDate = moment(item.date2, "YYYY/MM/DD");
        return itemDate.isBetween(last7dStart, yesterdayEnd, 'day', '[]');
      });
      const last7dValue = last7dData.reduce((sum, item) => sum + (item.value || 0), 0);
      
      // Calculate last 30 days data (total from the API response)
      const last30dValue = data.reduce((sum, item) => sum + (item.value || 0), 0);
      
      // Calculate trends by comparing with previous periods
      const prev30dStart = last30dStart.clone().subtract(30, 'days');
      const prev30dEnd = last30dStart.clone().subtract(1, 'day');
      
      const prevPayload = { ...payload };
      prevPayload.start_date = prev30dStart.format("YYYY-MM-DD");
      prevPayload.end_date = prev30dEnd.format("YYYY-MM-DD");
      
      try {
        const prevResponse = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, prevPayload);
        const prevData = prevResponse.data || [];
        
        // Calculate previous period values
        const prevYesterdayStart = yesterdayStart.clone().subtract(1, 'day');
        const prevYesterdayEnd = yesterdayStart.clone().subtract(1, 'day');
        const prevYesterdayData = prevData.filter(item => {
          const itemDate = moment(item.date2, "YYYY/MM/DD");
          return itemDate.isBetween(prevYesterdayStart, prevYesterdayEnd, 'day', '[]');
        });
        const prevYesterdayValue = prevYesterdayData.reduce((sum, item) => sum + (item.value || 0), 0);
        
        const prev7dStart = last7dStart.clone().subtract(7, 'days');
        const prev7dEnd = last7dStart.clone().subtract(1, 'day');
        const prev7dData = prevData.filter(item => {
          const itemDate = moment(item.date2, "YYYY/MM/DD");
          return itemDate.isBetween(prev7dStart, prev7dEnd, 'day', '[]');
        });
        const prev7dValue = prev7dData.reduce((sum, item) => sum + (item.value || 0), 0);
        
        const prev30dValue = prevData.reduce((sum, item) => sum + (item.value || 0), 0);
        
        // Calculate trends by comparing with averages
        const yesterdayTrend = yesterdayValue > parseFloat(averages.daily) ? 1 : yesterdayValue < parseFloat(averages.daily) ? -1 : 0;
        const last7dTrend = averages.weekly ? (last7dValue > parseFloat(averages.weekly) ? 1 : last7dValue < parseFloat(averages.weekly) ? -1 : 0) : null;
        const last30dTrend = averages.monthly ? (last30dValue > parseFloat(averages.monthly) ? 1 : last30dValue < parseFloat(averages.monthly) ? -1 : 0) : null;
        
        setRecentData({
          yesterday: { value: yesterdayValue, trend: yesterdayTrend },
          last7Days: { value: last7dValue, trend: last7dTrend },
          last30Days: { value: last30dValue, trend: last30dTrend }
        });
      } catch (error) {
        console.error("Error fetching previous period data:", error);
        // Calculate trends by comparing with averages when previous data is not available
        const yesterdayTrend = yesterdayValue > parseFloat(averages.daily) ? 1 : yesterdayValue < parseFloat(averages.daily) ? -1 : 0;
        const last7dTrend = averages.weekly ? (last7dValue > parseFloat(averages.weekly) ? 1 : last7dValue < parseFloat(averages.weekly) ? -1 : 0) : null;
        const last30dTrend = averages.monthly ? (last30dValue > parseFloat(averages.monthly) ? 1 : last30dValue < parseFloat(averages.monthly) ? -1 : 0) : null;
        
        setRecentData({
          yesterday: { value: yesterdayValue, trend: yesterdayTrend },
          last7Days: { value: last7dValue, trend: last7dTrend },
          last30Days: { value: last30dValue, trend: last30dTrend }
        });
      }
    } catch (error) {
      console.error("Error fetching recent data:", error);
    } finally {
      setIsLoadingRecent(false);
    }
  };

  useEffect(() => {
    fetchThroughputData();
  }, [effectiveStartRange, effectiveEndRange, effectiveCompanyFilter]);

  useEffect(() => {
    fetchRecentData();
  }, [effectiveCompanyFilter, averages]);

  // Process data for table display
  const tableData = useMemo(() => {
    if (!throughputData.length) return [];

    const processedData = processThroughputData(
      throughputData,
      tableViewType,
      "YYYY-MM-DD",
      effectiveStartRange ? moment(effectiveStartRange).format("YYYY-MM-DD") : null,
      effectiveEndRange ? moment(effectiveEndRange).format("YYYY-MM-DD") : null
    );

    // Transform data for horizontal table
    const transformedData = [];
    const periods = [...new Set(processedData.map(item => item.date2))].sort((a, b) => {
      const dateA = moment(a, ["YYYY/MM/DD", "MMM DD - MMM DD YYYY", "MMM YYYY"]);
      const dateB = moment(b, ["YYYY/MM/DD", "MMM DD - MMM DD YYYY", "MMM YYYY"]);
      return dateA.diff(dateB);
    });

    // Create a single row with all periods as columns
    const rowData = {
      key: 'throughput',
      period: 'Models Delivered'
    };

    periods.forEach(period => {
      const periodData = processedData.find(item => item.date2 === period);
      rowData[period] = periodData ? periodData.value : 0;
    });

    transformedData.push(rowData);
    return transformedData;
  }, [throughputData, tableViewType, effectiveStartRange, effectiveEndRange]);

  const tableColumns = useMemo(() => {
    if (!throughputData.length) return [];

    const processedData = processThroughputData(
      throughputData,
      tableViewType,
      "YYYY-MM-DD",
      effectiveStartRange ? moment(effectiveStartRange).format("YYYY-MM-DD") : null,
      effectiveEndRange ? moment(effectiveEndRange).format("YYYY-MM-DD") : null
    );

    const periods = [...new Set(processedData.map(item => item.date2))].sort((a, b) => {
      const dateA = moment(a, ["YYYY/MM/DD", "MMM DD - MMM DD YYYY", "MMM YYYY"]);
      const dateB = moment(b, ["YYYY/MM/DD", "MMM DD - MMM DD YYYY", "MMM YYYY"]);
      return dateA.diff(dateB);
    });

    // Create columns dynamically
    const columns = [
      {
        title: 'Period',
        dataIndex: 'period',
        key: 'period',
        fixed: 'left',
        width: 150,
        render: (text) => <strong style={{ fontSize: '12px', color: '#262626' }}>{text}</strong>
      }
    ];

    periods.forEach(period => {
      columns.push({
        title: (
          <Tooltip title={formatDateForTooltip(period)}>
            <span style={{ fontSize: '11px', fontWeight: 500 }}>{formatPeriodForTable(period, tableViewType)}</span>
          </Tooltip>
        ),
        dataIndex: period,
        key: period,
        width: tableViewType === "Daily" ? 80 : tableViewType === "Weekly" ? 100 : 70,
        align: 'center',
        render: (value) => <span style={{ fontSize: '12px', fontWeight: 500, color: '#262626' }}>{value.toLocaleString()}</span>
      });
    });

    return columns;
  }, [throughputData, tableViewType, effectiveStartRange, effectiveEndRange]);

  const renderTrendIcon = (trend) => {
    if (trend > 0) return <ArrowUpOutlined style={{ color: '#52c41a' }} />;
    if (trend < 0) return <ArrowDownOutlined style={{ color: '#ff4d4f' }} />;
    return null;
  };

  return (
    <Col span={24}>
      <Card style={chartStyle}>
        <Title level={4} style={{ marginBottom: 24, fontSize: '18px', fontWeight: 600 }}>
          Throughput Details
        </Title>

        {/* Averages Section */}
        <Row gutter={[24, 16]} style={{ marginBottom: 32 }}>
          <Col span={8}>
            <Card 
              size="small" 
              style={{ 
                borderRadius: '8px',
                border: 'none'
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px', color: '#595959' }}>Daily Average</span>}
                value={averages.daily}
                suffix={<span style={{ fontSize: '12px', color: '#8c8c8c' }}>models/day</span>}
                precision={1}
                valueStyle={{ fontSize: '20px', fontWeight: 600, color: '#262626' }}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card 
              size="small" 
              style={{ 
                borderRadius: '8px',
                border: 'none'
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px', color: '#595959' }}>Weekly Average</span>}
                value={averages.weekly || '—'}
                suffix={
                  averages.weekly ? 
                  <span style={{ fontSize: '12px', color: '#8c8c8c' }}>models/week</span> : 
                  <Tooltip title="Increase date range to get weekly average data" placement="right">
                    <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px', marginLeft: '4px' }} />
                  </Tooltip>
                }
                precision={1}
                valueStyle={{ fontSize: '20px', fontWeight: 600, color: averages.weekly ? '#262626' : '#8c8c8c' }}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card 
              size="small" 
              style={{ 
                borderRadius: '8px',
                border: 'none'
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px', color: '#595959' }}>Monthly Average</span>}
                value={averages.monthly || '—'}
                suffix={
                  averages.monthly ? 
                  <span style={{ fontSize: '12px', color: '#8c8c8c' }}>models/month</span> : 
                  <Tooltip title="Increase date range to get monthly average data" placement="right">
                    <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px', marginLeft: '4px' }} />
                  </Tooltip>
                }
                precision={1}
                valueStyle={{ fontSize: '20px', fontWeight: 600, color: averages.monthly ? '#262626' : '#8c8c8c' }}
                loading={isLoading}
              />
            </Card>
          </Col>
        </Row>

        {/* Recent Values Section */}
        <Row gutter={[24, 16]} style={{ marginBottom: 32 }}>
          <Col span={24}>
            <Title level={5} style={{ marginBottom: 16, fontSize: '16px', fontWeight: 500, color: '#262626' }}>
                Recent Metrics
            </Title>
          </Col>
          <Col span={8}>
            <Card 
              size="small" 
              style={{ 
                borderRadius: '8px',
                border: 'none'
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px', color: '#595959' }}>Yesterday</span>}
                value={recentData.yesterday.value}
                suffix={renderTrendIcon(recentData.yesterday.trend)}
                loading={isAnyLoading}
                valueStyle={{ fontSize: '18px', fontWeight: 600, color: '#262626' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card 
              size="small" 
              style={{ 
                borderRadius: '8px',
                border: 'none'
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px', color: '#595959' }}>Last 7 Days</span>}
                value={recentData.last7Days.value}
                suffix={renderTrendIcon(recentData.last7Days.trend)}
                loading={isAnyLoading}
                valueStyle={{ fontSize: '18px', fontWeight: 600, color: '#262626' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card 
              size="small" 
              style={{ 
                borderRadius: '8px',
                border: 'none'
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px', color: '#595959' }}>Last 30 Days</span>}
                value={recentData.last30Days.value}
                suffix={renderTrendIcon(recentData.last30Days.trend)}
                loading={isAnyLoading}
                valueStyle={{ fontSize: '18px', fontWeight: 600, color: '#262626' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Table Section */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
              <Title level={5} style={{ margin: 0, fontSize: '16px', fontWeight: 500, color: '#262626' }}>
                Detailed Breakdown
              </Title>
              <Space size="small">
                <Button
                  type={tableViewType === "Daily" ? "primary" : "default"}
                  size="small"
                  onClick={() => setTableViewType("Daily")}
                  style={{ 
                    fontSize: '12px',
                    height: '28px',
                    borderRadius: '6px'
                  }}
                >
                  Daily
                </Button>
                <Button
                  type={tableViewType === "Weekly" ? "primary" : "default"}
                  size="small"
                  onClick={() => setTableViewType("Weekly")}
                  style={{ 
                    fontSize: '12px',
                    height: '28px',
                    borderRadius: '6px'
                  }}
                >
                  Weekly
                </Button>
                <Button
                  type={tableViewType === "Monthly" ? "primary" : "default"}
                  size="small"
                  onClick={() => setTableViewType("Monthly")}
                  style={{ 
                    fontSize: '12px',
                    height: '28px',
                    borderRadius: '6px'
                  }}
                >
                  Monthly
                </Button>
              </Space>
            </Row>
            <div style={{ 
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}>
              <Table
                columns={tableColumns}
                dataSource={tableData}
                pagination={false}
                loading={isLoading}
                size="small"
                scroll={{ x: 'max-content' }}
                bordered={false}
                style={{ fontSize: '12px' }}
                rowClassName={() => 'analytics-table-row-increase-height'}
              />
            </div>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

export default ThroughputDetails;
